import { z } from 'zod';
import { ANSWER_TYPES, REACTION_VALUES, UPLOAD_CONTENT_TYPES, UPLOAD_KINDS, UPLOAD_MAX_BYTES, ARTICLE_STATUSES, } from './enums.js';
import { paginationQuerySchema } from './pagination.js';
import { REGIONS } from './regions.js';
import { GRANT_KIND_WON_KEYS, IDENTITY_KEYS, VISIT_REASON_KEYS } from './identity.js';
import { COMMUNITY_TOPIC_KEYS } from './community.js';
/**
 * Zod request-validation schemas at the API boundary.
 * Validation failures map to 422.
 */
export const answerTypeSchema = z.enum(ANSWER_TYPES);
/**
 * Max serialized size of a tiptap document body (chars of JSON.stringify).
 * A boundary cap that bounds parse/recursion cost (DoS defense) while staying
 * far above any legitimate article/survey body. Exceeding it → 422.
 */
export const BODY_MAX_SERIALIZED = 200_000;
/**
 * tiptap document body (jsonb): opaque JSON object at the contract boundary
 * (tiptap controls structure/rendering, XSS-safe). Bounded by serialized size
 * so a pathological/huge body is rejected before it reaches the extractors.
 */
const bodySchema = z
    .record(z.unknown())
    .refine((b) => JSON.stringify(b).length <= BODY_MAX_SERIALIZED, {
    message: `body exceeds ${BODY_MAX_SERIALIZED} serialized characters`,
});
/* ----------------------------------------------------------------------------
 * users — profile (GET/PATCH /users/me)
 * -------------------------------------------------------------------------- */
export const NICKNAME_MIN = 2;
export const NICKNAME_MAX = 16;
/**
 * Profile update (PATCH /users/me) — 값 하나씩 고치는 마이페이지 팝업이 보낸다.
 * 닉네임 · 관심 지역 · 정체성 · 방문 이유 · 누적 꽁돈(횟수 + 종류 한 쌍). 하나 이상 있어야 한다.
 */
export const updateMeSchema = z
    .object({
    nickname: z.string().trim().min(NICKNAME_MIN).max(NICKNAME_MAX).optional(),
    /** 관심 지역. 빈 배열 = 전부 지움. */
    regions: z.array(z.enum(REGIONS)).max(REGIONS.length).optional(),
    identity: z.enum(IDENTITY_KEYS).optional(),
    visitReason: z.enum(VISIT_REASON_KEYS).optional(),
    /** 0~4 는 그 횟수, 5 는 「5회 이상」. 1 이상이면 grantKinds 가 하나 이상 있어야 한다. */
    grantWins: z.number().int().min(0).max(5).optional(),
    grantKinds: z.array(z.enum(GRANT_KIND_WON_KEYS)).max(GRANT_KIND_WON_KEYS.length).optional(),
})
    .refine((v) => v.nickname !== undefined || v.regions !== undefined || v.identity !== undefined
    || v.visitReason !== undefined || v.grantWins !== undefined, { message: 'At least one of nickname, regions, identity, visitReason, grantWins must be provided' })
    .refine((v) => v.grantKinds === undefined || v.grantWins !== undefined, {
    message: 'grantKinds requires grantWins',
    path: ['grantKinds'],
})
    .refine((v) => v.grantWins === undefined || v.grantWins === 0 || (v.grantKinds?.length ?? 0) > 0, {
    message: '따먹어 본 적이 있다면 무엇을 따먹었는지 하나는 골라야 한다',
    path: ['grantKinds'],
});
/** Onboarding (POST /users/me/onboarding) — 정체성 네 문항. Required (unlike PATCH, which is partial). */
export const onboardingSchema = z
    .object({
    /** 1. 당신은 누구십니까? */
    identity: z.enum(IDENTITY_KEYS),
    /** 2. 그래서 여기는 왜 오셨나요? */
    visitReason: z.enum(VISIT_REASON_KEYS),
    /** 3. 지금까지 몇 번이나 꽁돈을 따먹어 보셨나요? 0~4 는 그 횟수, 5 는 「5회 이상」. */
    grantWins: z.number().int().min(0).max(5),
    /** 4. (3 이 1 이상이면) 어떤 걸 따먹어 봤나요? */
    grantKinds: z.array(z.enum(GRANT_KIND_WON_KEYS)).max(GRANT_KIND_WON_KEYS.length).default([]),
})
    .refine((v) => v.grantWins === 0 || v.grantKinds.length > 0, {
    message: '따먹어 본 적이 있다면 무엇을 따먹었는지 하나는 골라 주세요',
    path: ['grantKinds'],
});
/* ----------------------------------------------------------------------------
 * posts — 커뮤니티 글 (설문은 선택 항목). 설계 2026-09-10.
 * -------------------------------------------------------------------------- */
/** Posts list query (GET /posts): 최신순 + cursor pagination. */
export const postsListQuerySchema = paginationQuerySchema.extend({
    /** 게시판(소주제). 없으면 전체. */
    topic: z.enum(COMMUNITY_TOPIC_KEYS).optional(),
    /** `1` 이면 내 글만 — 로그인 필요(마이페이지 「나의 커뮤니티」). */
    mine: z.literal('1').optional(),
    /** latest(기본) | popular(조회수 순, 홈 「인기글」). */
    sort: z.enum(['latest', 'popular']).default('latest'),
});
export const POST_TITLE_MIN = 2;
export const POST_TITLE_MAX = 150;
export const POST_TAGS_MAX = 10;
export const OPTION_LABEL_MIN = 1;
export const OPTION_LABEL_MAX = 120;
export const SURVEY_OPTIONS_MIN = 2;
export const SURVEY_OPTIONS_MAX = 20;
export const surveyOptionInputSchema = z.object({
    label: z.string().trim().min(OPTION_LABEL_MIN).max(OPTION_LABEL_MAX),
});
/**
 * 글에 붙이는 설문. 보기 2개 이상. `closesAt` 은 선택이며 있으면 미래여야 한다.
 */
export const surveyInputSchema = z
    .object({
    answerType: answerTypeSchema,
    options: z.array(surveyOptionInputSchema).min(SURVEY_OPTIONS_MIN).max(SURVEY_OPTIONS_MAX),
    closesAt: z.string().datetime().nullable().optional(),
})
    .refine((v) => v.closesAt == null || new Date(v.closesAt) > new Date(), {
    message: 'closesAt must be in the future',
    path: ['closesAt'],
});
const postTagsSchema = z.array(z.string().trim().min(1).max(30)).max(POST_TAGS_MAX);
/** Post create (POST /posts). `survey` 가 있으면 글과 설문을 한 트랜잭션으로 만든다. */
export const createPostSchema = z.object({
    /** 게시판(소주제). 글은 반드시 하나에 속한다. */
    topic: z.enum(COMMUNITY_TOPIC_KEYS),
    title: z.string().trim().min(POST_TITLE_MIN).max(POST_TITLE_MAX),
    body: bodySchema,
    tags: postTagsSchema.optional(),
    survey: surveyInputSchema.nullable().optional(),
});
/**
 * Post update (PATCH /posts/:id). 부분 갱신, 하나는 있어야 한다.
 * `survey`: 객체면 교체(투표가 하나도 없을 때만 — 서비스가 409), null 이면 설문 제거.
 */
export const updatePostSchema = z
    .object({
    topic: z.enum(COMMUNITY_TOPIC_KEYS).optional(),
    title: z.string().trim().min(POST_TITLE_MIN).max(POST_TITLE_MAX).optional(),
    body: bodySchema.optional(),
    tags: postTagsSchema.optional(),
    survey: surveyInputSchema.nullable().optional(),
})
    .refine((v) => v.topic !== undefined || v.title !== undefined || v.body !== undefined || v.tags !== undefined || v.survey !== undefined, { message: 'At least one field must be provided' });
/* ----------------------------------------------------------------------------
 * votes — PUT /posts/:id/vote (state-set, idempotent)
 * -------------------------------------------------------------------------- */
/**
 * Vote set. `optionIds` is the viewer's complete selection set:
 *   - single answer type → exactly 1 id (enforced server-side against the survey)
 *   - multiple answer type → 1..n ids
 * An empty set is rejected here (use DELETE /vote to clear).
 */
export const voteSchema = z.object({
    optionIds: z.array(z.string().uuid()).min(1),
});
/**
 * Reaction set (PUT /{posts|articles}/:id/reaction). `value` is the viewer's
 * single reaction; sending it again with the opposite value switches sides, and
 * DELETE clears it. State-set design → idempotent (mirrors the vote contract).
 */
export const reactionSchema = z.object({
    value: z.enum(REACTION_VALUES),
});
/* ----------------------------------------------------------------------------
 * comments — POST /{posts|articles|grants}/:id/comments, PATCH /comments/:id
 * -------------------------------------------------------------------------- */
export const COMMENT_BODY_MIN = 1;
export const COMMENT_BODY_MAX = 2000;
/**
 * Comment create (POST /{posts|articles|grants}/:id/comments). `parentId` is optional:
 *   - absent → top-level comment;
 *   - present → reply. The server caps depth at 2 (replying to a reply flattens
 *     onto its top-level parent) and verifies the parent is on the same target.
 */
/** POST /grants/:id/report — 잘못된 내용 신고. 본문만 있다. 저장하지 않고 Slack 으로 보낸다. */
export const grantReportSchema = z.object({
    body: z.string().trim().min(1).max(COMMENT_BODY_MAX),
});
export const createCommentSchema = z.object({
    body: z.string().trim().min(COMMENT_BODY_MIN).max(COMMENT_BODY_MAX),
    parentId: z.string().uuid().optional(),
});
// Update is body-only — parentId is immutable after creation.
export const updateCommentSchema = z.object({
    body: z.string().trim().min(COMMENT_BODY_MIN).max(COMMENT_BODY_MAX),
});
/* ----------------------------------------------------------------------------
 * uploads — POST /uploads/sign (R2 presigned PUT)
 * -------------------------------------------------------------------------- */
/**
 * Upload-sign request. The server pins `contentType` (allowlist) AND `size`
 * (Content-Length) into the signed URL; both are rejected at the boundary (422)
 * before any R2 call. `size` is REQUIRED so the signature can constrain the
 * upload to the exact byte length the client declares (no arbitrary-size PUTs,
 *). The browser then PUTs the bytes (with that Content-Length) to R2.
 */
export const signUploadSchema = z.object({
    contentType: z.enum(UPLOAD_CONTENT_TYPES),
    size: z.number().int().positive().max(UPLOAD_MAX_BYTES),
    /**
     * Upload purpose — namespaces the object key (e.g. 'article' → articles/...).
     * Optional for backward compatibility; absent → legacy per-user namespace.
     */
    kind: z.enum(UPLOAD_KINDS).optional(),
});
/* ----------------------------------------------------------------------------
 * diagnoses — V3 Pillar 1: AI counselor (OpenAI)
 *
 * Boundary validation; failures → 422. The user submits free-text
 * describing their organization; the server forwards a structured prompt to
 * OpenAI and stores a fixed-schema report. Inputs are private to the owner —
 * never serialized to other viewers. Length caps below double as the abuse /
 * token-cost guard (the create endpoint is also rate-limited).
 * -------------------------------------------------------------------------- */
export const DIAGNOSIS_TEXT_MIN = 1;
/** Per-field cap — bounds the prompt size sent to OpenAI (cost / abuse guard). */
export const DIAGNOSIS_TEXT_MAX = 4000;
export const DIAGNOSIS_NOTE_MAX = 1000;
/** BEP (break-even point) achievement state. */
export const BEP_STATES = ['yes', 'no', 'unknown'];
export const bepStateSchema = z.enum(BEP_STATES);
/**
 * Diagnosis create (POST /diagnoses) — the organization-diagnosis inputs.
 *
 * Core fields (memberRoles / companyGoals / productivity) are required so the
 * report has enough signal; KPIs / BEP detail are optional context. `bepAchieved`
 * is a fixed enum; `bepNote` is an optional free-text explanation.
 */
export const createDiagnosisSchema = z.object({
    /** Members' responsibilities / duties / role pros & cons. */
    memberRoles: z.string().trim().min(DIAGNOSIS_TEXT_MIN).max(DIAGNOSIS_TEXT_MAX),
    /** Company goals. */
    companyGoals: z.string().trim().min(DIAGNOSIS_TEXT_MIN).max(DIAGNOSIS_TEXT_MAX),
    /** Each member's KPIs (optional context). */
    kpis: z.string().trim().max(DIAGNOSIS_TEXT_MAX).optional(),
    /** Break-even point achievement. */
    bepAchieved: bepStateSchema,
    /** Optional explanation of the BEP state. */
    bepNote: z.string().trim().max(DIAGNOSIS_NOTE_MAX).optional(),
    /** What is productive vs. unproductive in the org. */
    productivity: z.string().trim().min(DIAGNOSIS_TEXT_MIN).max(DIAGNOSIS_TEXT_MAX),
});
/* ----------------------------------------------------------------------------
 * articles — V4 Pillar 4: 조쏘 PD 수첩
 *
 * Boundary validation; failures → 422. `body` is the opaque tiptap doc
 * (XSS-safe, tiptap-controlled). `thumbnail_url` is NOT a client input — the
 * server derives it from the first body image at write time. `isActive` is
 * never client-set on create/update; it is admin-only via /articles/:id/active.
 * -------------------------------------------------------------------------- */
export const ARTICLE_TITLE_MIN = 2;
export const ARTICLE_TITLE_MAX = 150;
export const ARTICLE_TAGS_MAX = 10;
export const ARTICLE_TAG_MIN = 1;
export const ARTICLE_TAG_MAX = 40;
/** tiptap document body (jsonb) — opaque JSON object at the contract boundary. */
export const articleBodySchema = bodySchema;
const articleTagSchema = z.string().trim().min(ARTICLE_TAG_MIN).max(ARTICLE_TAG_MAX);
/**
 * Article create (POST /articles). Created with isActive=false (admin-approval
 * gate). tags optional.
 */
export const createArticleSchema = z.object({
    title: z.string().trim().min(ARTICLE_TITLE_MIN).max(ARTICLE_TITLE_MAX),
    body: articleBodySchema,
    tags: z.array(articleTagSchema).max(ARTICLE_TAGS_MAX).optional(),
    /**
     * true → submit for review immediately (검수 대기); absent/false → save as a
     * draft (초안, author-only, never enters the admin queue).
     */
    submit: z.boolean().optional(),
});
/** Article update (PATCH /articles/:id) — partial; at least one field. */
export const updateArticleSchema = z
    .object({
    title: z.string().trim().min(ARTICLE_TITLE_MIN).max(ARTICLE_TITLE_MAX).optional(),
    body: articleBodySchema.optional(),
    tags: z.array(articleTagSchema).max(ARTICLE_TAGS_MAX).nullable().optional(),
})
    .refine((v) => v.title !== undefined || v.body !== undefined || v.tags !== undefined, { message: 'At least one field must be provided' });
/** Admin approval toggle (PATCH /articles/:id/active, admin-only). */
export const setArticleActiveSchema = z.object({
    isActive: z.boolean(),
});
/** PATCH /articles/:id/status (admin). 초안·검수 대기·발행 세 상태를 한 번에 정한다 — 콘솔 편집 화면용. */
export const setArticleStatusSchema = z.object({
    status: z.enum(ARTICLE_STATUSES),
});
/**
 * Articles list query (GET /articles): optional tag filter over cursor
 * pagination.
 */
export const articlesListQuerySchema = paginationQuerySchema.extend({
    tag: z.string().trim().min(ARTICLE_TAG_MIN).max(ARTICLE_TAG_MAX).optional(),
});
//# sourceMappingURL=schemas.js.map